// attachmentCache.ts
// Кэш скачанных вложений (PDF/Word) в IndexedDB. Без React, без внешних импортов —
// переиспользуется и в pda-cfront. Ни одна экспортируемая функция не бросает: любая
// ошибка кэша деградирует в «промах», просмотр файла не должен ломаться никогда.

export const CACHE_TOTAL_CAP_BYTES = 600 * 1024 * 1024;
export const CACHE_MAX_FILE_BYTES = 400 * 1024 * 1024;

const DB_NAME = 'pda-attachment-cache';
const DB_VERSION = 1;
const FILES_STORE = 'files';
const META_STORE = 'meta';
const META_INDEX_LAST_USED = 'by_lastUsedAt';
const OPEN_TIMEOUT_MS = 3000;

export interface CachedFile {
    buffer: ArrayBuffer;
    type: string;
}

interface FileRecord {
    id: string;
    buffer: ArrayBuffer;
    type: string;
}

interface MetaRecord {
    id: string;
    size: number;
    type: string;
    lastUsedAt: number;
}

type CacheState = 'unknown' | 'ok' | 'disabled';

let state: CacheState = 'unknown';
let dbPromise: Promise<IDBDatabase | null> | null = null;

function disable(): null {
    state = 'disabled';
    return null;
}

/**
 * Промис реального indexedDB.open() — единожды создаётся и кэшируется в dbPromise,
 * никогда не резолвится таймаутом. Раньше таймаут в openDb() резолвил именно этот
 * кэшированный промис в null и тем самым «отравлял» его навсегда: даже если
 * настоящий open() потом успешно завершался, все последующие вызовы openDb()
 * продолжали получать null (промис уже settled). На загруженной странице (первый
 * open(), HMR, монтирование React) 3-секундный таймаут легко проигрывает гонку —
 * после чего кэш молча переставал работать до перезагрузки страницы.
 */
function createRealOpenPromise(): Promise<IDBDatabase | null> {
    return new Promise<IDBDatabase | null>((resolve) => {
        if (typeof indexedDB === 'undefined') {
            resolve(disable());
            return;
        }

        let request: IDBOpenDBRequest;
        try {
            request = indexedDB.open(DB_NAME, DB_VERSION);
        } catch {
            resolve(disable());
            return;
        }

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(FILES_STORE)) {
                db.createObjectStore(FILES_STORE, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(META_STORE)) {
                const metaStore = db.createObjectStore(META_STORE, { keyPath: 'id' });
                metaStore.createIndex(META_INDEX_LAST_USED, 'lastUsedAt');
            }
        };

        request.onsuccess = () => {
            const db = request.result;
            db.onversionchange = () => {
                db.close();
                state = 'disabled';
                dbPromise = null;
            };
            state = 'ok';
            resolve(db);
        };

        request.onerror = () => resolve(disable());
        request.onblocked = () => resolve(disable());
    });
}

function openDb(): Promise<IDBDatabase | null> {
    if (state === 'disabled') return Promise.resolve(null);
    if (!dbPromise) dbPromise = createRealOpenPromise();

    // Гонка с таймаутом — только для текущего вызова: если open() подвиснет,
    // этот вызов получит null и просмотрщик уйдёт в сеть, но dbPromise
    // по-прежнему указывает на реальный промис, и следующий вызов подхватит
    // его результат, когда (если) open() всё-таки завершится.
    return Promise.race([
        dbPromise,
        new Promise<IDBDatabase | null>((resolve) => setTimeout(() => resolve(null), OPEN_TIMEOUT_MS)),
    ]);
}

function reqToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function txDone(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
}

async function readAllMeta(db: IDBDatabase): Promise<MetaRecord[]> {
    const tx = db.transaction(META_STORE, 'readonly');
    const all = await reqToPromise(tx.objectStore(META_STORE).getAll());
    return all as MetaRecord[];
}

export async function getCachedFile(id: string): Promise<CachedFile | null> {
    try {
        const db = await openDb();
        if (!db) return null;

        const tx = db.transaction(FILES_STORE, 'readonly');
        const record = (await reqToPromise(tx.objectStore(FILES_STORE).get(id))) as FileRecord | undefined;
        if (!record) return null;

        void touchLastUsed(db, id);
        return { buffer: record.buffer, type: record.type };
    } catch {
        return null;
    }
}

async function touchLastUsed(db: IDBDatabase, id: string): Promise<void> {
    try {
        const tx = db.transaction(META_STORE, 'readwrite');
        const store = tx.objectStore(META_STORE);
        const meta = (await reqToPromise(store.get(id))) as MetaRecord | undefined;
        if (meta) {
            store.put({ ...meta, lastUsedAt: Date.now() });
        }
        await txDone(tx);
    } catch {
        // деградация качества LRU, не критично
    }
}

async function evictToFit(db: IDBDatabase, incomingId: string, incomingSize: number): Promise<void> {
    const metas = await readAllMeta(db);
    let total = metas.reduce((sum, m) => (m.id === incomingId ? sum : sum + m.size), 0);
    if (total + incomingSize <= CACHE_TOTAL_CAP_BYTES) return;

    const victims = metas
        .filter((m) => m.id !== incomingId)
        .sort((a, b) => a.lastUsedAt - b.lastUsedAt);

    const tx = db.transaction([FILES_STORE, META_STORE], 'readwrite');
    const filesStore = tx.objectStore(FILES_STORE);
    const metaStore = tx.objectStore(META_STORE);

    for (const victim of victims) {
        if (total + incomingSize <= CACHE_TOTAL_CAP_BYTES) break;
        filesStore.delete(victim.id);
        metaStore.delete(victim.id);
        total -= victim.size;
    }

    await txDone(tx);
}

async function writeFile(db: IDBDatabase, id: string, buffer: ArrayBuffer, type: string): Promise<void> {
    const tx = db.transaction([FILES_STORE, META_STORE], 'readwrite');
    tx.objectStore(FILES_STORE).put({ id, buffer, type } satisfies FileRecord);
    tx.objectStore(META_STORE).put({ id, size: buffer.byteLength, type, lastUsedAt: Date.now() } satisfies MetaRecord);
    await txDone(tx);
}

export async function putCachedFile(id: string, buffer: ArrayBuffer, type: string): Promise<void> {
    if (buffer.byteLength > CACHE_MAX_FILE_BYTES) return;

    try {
        const db = await openDb();
        if (!db) return;

        await evictToFit(db, id, buffer.byteLength);

        try {
            await writeFile(db, id, buffer, type);
        } catch (err) {
            if (err instanceof DOMException && err.name === 'QuotaExceededError') {
                await evictToHalfCap(db, id);
                try {
                    await writeFile(db, id, buffer, type);
                } catch {
                    state = 'disabled';
                }
            }
        }
    } catch {
        // промах записи не должен ломать просмотр файла
    }
}

async function evictToHalfCap(db: IDBDatabase, incomingId: string): Promise<void> {
    const metas = await readAllMeta(db);
    let total = metas.reduce((sum, m) => (m.id === incomingId ? sum : sum + m.size), 0);
    const halfCap = CACHE_TOTAL_CAP_BYTES / 2;

    const victims = metas
        .filter((m) => m.id !== incomingId)
        .sort((a, b) => a.lastUsedAt - b.lastUsedAt);

    const tx = db.transaction([FILES_STORE, META_STORE], 'readwrite');
    const filesStore = tx.objectStore(FILES_STORE);
    const metaStore = tx.objectStore(META_STORE);

    for (const victim of victims) {
        if (total <= halfCap) break;
        filesStore.delete(victim.id);
        metaStore.delete(victim.id);
        total -= victim.size;
    }

    await txDone(tx);
}

export async function clearAttachmentCache(): Promise<void> {
    try {
        const db = await openDb();
        if (!db) return;

        const tx = db.transaction([FILES_STORE, META_STORE], 'readwrite');
        tx.objectStore(FILES_STORE).clear();
        tx.objectStore(META_STORE).clear();
        await txDone(tx);
    } catch {
        // нечего чистить или кэш недоступен
    }
}
